import { useState, useEffect, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Dropdown,
  Field,
  Option,
} from '@fluentui/react-components';
import { type UseMutationResult } from '@tanstack/react-query';
import { type TaxReturn, type SystemUser, type StaffRole } from '../../../types';
import { filterUsersByRole } from './staffFilters';

export interface EditStaffDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialPreparerId: string;
  initialReviewerId: string;
  systemUsers: SystemUser[] | undefined;
  staffRoles: StaffRole[] | undefined;
  updateMutation: UseMutationResult<unknown, Error, Partial<TaxReturn>>;
}

export function EditStaffDialog({
  open,
  onOpenChange,
  initialPreparerId,
  initialReviewerId,
  systemUsers,
  staffRoles,
  updateMutation,
}: EditStaffDialogProps) {
  const [selectedPreparer, setSelectedPreparer] = useState(initialPreparerId);
  const [selectedReviewer, setSelectedReviewer] = useState(initialReviewerId);

  useEffect(() => {
    if (open) {
      setSelectedPreparer(initialPreparerId);
      setSelectedReviewer(initialReviewerId);
    }
  }, [open, initialPreparerId, initialReviewerId]);

  const roleMap = useMemo(
    () => new Map((staffRoles ?? []).map(r => [r.systemUserId, r])),
    [staffRoles]
  );

  // Dropdowns are filtered to role-holders, keeping the currently-assigned person.
  const preparerOptions = useMemo(
    () => filterUsersByRole(systemUsers ?? [], roleMap, 'preparer', initialPreparerId),
    [systemUsers, roleMap, initialPreparerId]
  );

  const reviewerOptions = useMemo(
    () => filterUsersByRole(systemUsers ?? [], roleMap, 'reviewer', initialReviewerId),
    [systemUsers, roleMap, initialReviewerId]
  );

  // Picking a preparer auto-fills the reviewer from that preparer's mapping (still overridable).
  const handlePreparerChange = (preparerId: string) => {
    setSelectedPreparer(preparerId);
    const mappedReviewerId = roleMap.get(preparerId)?.reviewerId;
    if (mappedReviewerId) {
      setSelectedReviewer(mappedReviewerId);
    }
  };

  const handleSave = () => {
    const updates: Partial<TaxReturn> = {};
    const preparer = systemUsers?.find(u => u.systemUserId === selectedPreparer);
    const reviewer = systemUsers?.find(u => u.systemUserId === selectedReviewer);

    if (selectedPreparer) {
      updates.preparerId = selectedPreparer;
      updates.preparerName = preparer?.fullName || '';
      updates.preparerEmail = preparer?.email || '';
    }
    if (selectedReviewer) {
      updates.reviewerId = selectedReviewer;
      updates.reviewerName = reviewer?.fullName || '';
      updates.reviewerEmail = reviewer?.email || '';
    }

    if (Object.keys(updates).length > 0) {
      updateMutation.mutate(updates, {
        onSuccess: () => {
          onOpenChange(false);
        },
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Edit Staff Assignment</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Field label="Preparer">
                <Dropdown
                  placeholder="Select preparer"
                  value={systemUsers?.find(u => u.systemUserId === selectedPreparer)?.fullName || ''}
                  selectedOptions={selectedPreparer ? [selectedPreparer] : []}
                  onOptionSelect={(_, data) => handlePreparerChange(data.optionValue as string)}
                  style={{ width: '100%' }}
                >
                  {preparerOptions.map((user) => (
                    <Option key={user.systemUserId} value={user.systemUserId}>
                      {user.fullName}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
              <Field label="Reviewer">
                <Dropdown
                  placeholder="Select reviewer"
                  value={systemUsers?.find(u => u.systemUserId === selectedReviewer)?.fullName || ''}
                  selectedOptions={selectedReviewer ? [selectedReviewer] : []}
                  onOptionSelect={(_, data) => setSelectedReviewer(data.optionValue as string)}
                  style={{ width: '100%' }}
                >
                  {reviewerOptions.map((user) => (
                    <Option key={user.systemUserId} value={user.systemUserId}>
                      {user.fullName}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={updateMutation.isPending}
              onClick={handleSave}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
